import {
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from '@mui/joy';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { Button, Confirmation } from 'core/components';
import {
  useCancelConsumerJobs,
  useGetConsumerJobs,
  useGetConsumerRestartTargets,
  useRestartConsumers,
} from 'dashboard/hooks';
import { IConsumerRestartTarget } from 'dashboard/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

import { ConsumerJobsTable } from './ConsumerJobsTable';
import { ConsumerLoadError } from './ConsumerLoadError';
import { JobsRefreshStatus } from './JobsRefreshStatus';
import { RestartButtons } from './RestartButtons';
import { hasLiveConsumer, isDevPoolEnv } from './utils';

const CARD_SX = { boxShadow: Size.SM };

const CONFIRM_INPUT_ID = 'consumer-restart-confirm-env';

const CONFIRM_HINT_ID = 'consumer-restart-confirm-hint';

/**
 * Mirrors COOLDOWN_SECONDS in Admin_ConsumerRestartService. The back-end is the
 * authority — it refuses the request either way, and covers two people clicking the
 * same env from different instances — this only spares a pointless click.
 */
const RESTART_COOLDOWN_MS = 30 * 1000;

export const ConsumerRestart = () => {
  const { t } = useTranslation();

  const [pendingTarget, setPendingTarget] = useState<IConsumerRestartTarget | null>(null);

  const [confirmText, setConfirmText] = useState('');

  const [selectedJobIds, setSelectedJobIds] = useState<number[]>([]);

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const [cooldownEnvs, setCooldownEnvs] = useState<string[]>([]);

  // Keyed by env and deleted when each fires, so the map cannot grow unbounded and
  // a restart that resolves after unmount has its timer cleared rather than leaked.
  const cooldownTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const { user } = useAuthUser();

  const isAdmin = !!user?.admin;

  // Mirrors the page's admin guard so the requests never leave the browser for a
  // non-admin, even though the endpoints refuse them anyway.
  const {
    isError: isTargetsError,
    isLoading: isTargetsLoading,
    refetchTargets,
    targets,
  } = useGetConsumerRestartTargets({ enabled: isAdmin });

  // Gated on there being at least one restartable target as well as admin. On a
  // deployment that has not opted into the feature, targetsAction degrades to an
  // empty list (the buttons card shows "no environments"), but jobsAction returns
  // 403 — so firing the jobs query there would raise a red ConsumerLoadError beside
  // the graceful empty targets card. Waiting for a target keeps both halves quiet
  // together when the feature is off, and the jobs card is hidden entirely in that
  // case so a prod admin sees only the empty targets note rather than an unfetched
  // "no jobs" list and a "Refreshing in 0s" countdown that never ran.
  const isJobsQueryEnabled = isAdmin && targets.length > 0;

  const {
    dataUpdatedAt,
    isError: isJobsError,
    isFetching: isJobsFetching,
    jobs,
    refetchJobs,
  } = useGetConsumerJobs({ enabled: isJobsQueryEnabled });

  const { restartConsumers, restartingEnv } = useRestartConsumers();

  const { cancelConsumerJobs, isCancelConsumerJobsLoading } = useCancelConsumerJobs();

  // Only jobs genuinely running on THIS env count as at risk, and they decide which
  // confirmation copy is shown. Queued jobs are untouched by a restart — they stay
  // queued and the fresh consumers pick them up — and stale rows are orphans whose
  // consumer is already gone, so neither is worth alarming about.
  const getRunningJobs = useCallback(
    (env: string) => jobs.filter((job) => hasLiveConsumer(job) && env === job.env),
    [jobs]
  );

  const runRestart = useCallback(
    async (env: string) => {
      try {
        await restartConsumers(env);
      } catch {
        // Already surfaced as a toast by useHandleErrors' onError. Swallowed only to
        // keep an HTTP-level failure off window.onunhandledrejection, and so the
        // refresh below still runs and shows the real state of the box.
      }

      setCooldownEnvs((current) => [...current, env]);

      cooldownTimers.current[env] = setTimeout(() => {
        delete cooldownTimers.current[env];

        setCooldownEnvs((current) => current.filter((item) => item !== env));
      }, RESTART_COOLDOWN_MS);

      // Both lists move: the restart marks whatever it interrupted as failed, and
      // the consumer count on the button is the number the user clicked because of.
      refetchJobs();
      refetchTargets();
    },
    [refetchJobs, refetchTargets, restartConsumers]
  );

  // Always confirm, even when nothing is at risk. A tester read the bare env buttons
  // as navigation and did not expect a click to restart anything, so the dialog is
  // the backstop that names the action before it happens.
  const onClickRestart = useCallback((target: IConsumerRestartTarget) => {
    setConfirmText('');
    setPendingTarget(target);
  }, []);

  const onCancelRestart = useCallback(() => {
    setConfirmText('');
    setPendingTarget(null);
  }, []);

  const onConfirmRestart = useCallback(() => {
    if (!pendingTarget) {
      return;
    }

    const { env } = pendingTarget;

    setConfirmText('');
    setPendingTarget(null);

    runRestart(env);
  }, [pendingTarget, runRestart]);

  const onToggleJob = useCallback((jobId: number) => {
    setSelectedJobIds((current) =>
      current.includes(jobId) ? current.filter((id) => id !== jobId) : [...current, jobId]
    );
  }, []);

  const onToggleAll = useCallback(
    (checked: boolean) => setSelectedJobIds(checked ? jobs.map(({ id }) => id) : []),
    [jobs]
  );

  const onConfirmCancelJobs = useCallback(async () => {
    setIsCancelConfirmOpen(false);

    try {
      await cancelConsumerJobs({ jobIds: selectedJobIds });
    } catch {
      // Toasted by useHandleErrors; see runRestart.
    }

    // Canceled rows leave the pending list, so a kept selection would point at rows
    // that are no longer shown.
    setSelectedJobIds([]);

    refetchJobs();
  }, [cancelConsumerJobs, refetchJobs, selectedJobIds]);

  useEffect(
    () => () => {
      Object.values(cooldownTimers.current).forEach(clearTimeout);
    },
    []
  );

  // The list polls every 30s, so a selected job can finish and disappear between
  // ticks. Dropping vanished ids keeps the Cancel count honest and stops the header
  // checkbox from being permanently un-checkable because the selection outnumbers
  // the visible rows. Returns the same array when nothing was pruned, so no loop.
  useEffect(() => {
    setSelectedJobIds((current) => {
      const stillVisible = current.filter((id) => jobs.some((job) => id === job.id));

      return stillVisible.length === current.length ? current : stillVisible;
    });
  }, [jobs]);

  // A poll can empty the selection while its confirmation is open, which would
  // otherwise leave a dialog offering to cancel nothing.
  useEffect(() => {
    if (!selectedJobIds.length) {
      setIsCancelConfirmOpen(false);
    }
  }, [selectedJobIds.length]);

  const pendingEnvLabel = (pendingTarget?.label ?? '').toUpperCase();

  // Shared environments are read by the whole team — and on uat and prod the only
  // target is a shared one — so those restarts ask for the name to be typed rather
  // than accepting a single click. Dev-pool envs keep the plain confirmation.
  const requiresTypedConfirm = !!pendingTarget && !isDevPoolEnv(pendingTarget.env);

  const isTypedConfirmUnmet =
    requiresTypedConfirm && confirmText.trim().toUpperCase() !== pendingEnvLabel;

  const affectedJobs = pendingTarget ? getRunningJobs(pendingTarget.env) : [];

  // Drives the cancel copy: true only when at least one selected job is genuinely
  // being executed, so cancelling would stop a consumer as well as the job.
  const selectedJobsKillConsumers = jobs.some(
    (job) => selectedJobIds.includes(job.id) && hasLiveConsumer(job)
  );

  return (
    <Stack spacing={2}>
      <Card sx={CARD_SX} variant={Variant.OUTLINED}>
        <CardContent>
          <Stack spacing={2}>
            {/* Title, help line, divider: the same card shape the sibling Manage Cache
                page uses for every group, so the two admin pages read as one family. */}
            <Stack spacing={0.5}>
              <Typography level={Level.TITLE_MD}>{t('titleConsumerRestartTargets')}</Typography>

              <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
                {t('messageConsumerRestartTargetsHelp')}
              </Typography>
            </Stack>

            <Divider />

            {isTargetsError ? (
              <ConsumerLoadError
                message={t('messageConsumerRestartTargetsError')}
                onRetry={refetchTargets}
              />
            ) : (
              <RestartButtons
                cooldownEnvs={cooldownEnvs}
                isLoading={isTargetsLoading}
                onRestart={onClickRestart}
                restartingEnv={restartingEnv}
                targets={targets}
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      {isJobsQueryEnabled && (
        <Card sx={CARD_SX} variant={Variant.OUTLINED}>
          <CardContent>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Stack alignItems="center" direction={Direction.ROW} spacing={1}>
                  <Typography level={Level.TITLE_MD}>{t('titleConsumerRestartJobs')}</Typography>

                  {!!jobs.length && (
                    <Chip
                      aria-label={t('labelConsumerJobCount', { count: jobs.length })}
                      color={Color.NEUTRAL}
                      size={Size.SM}
                      variant={Variant.SOFT}
                    >
                      {jobs.length}
                    </Chip>
                  )}

                  <JobsRefreshStatus
                    dataUpdatedAt={dataUpdatedAt}
                    isFetching={isJobsFetching}
                    isStalled={isJobsError}
                    onRefresh={refetchJobs}
                  />

                  <Button
                    color={Color.DANGER}
                    hide={!selectedJobIds.length}
                    loading={isCancelConsumerJobsLoading}
                    onClick={() => setIsCancelConfirmOpen(true)}
                    size={Size.SM}
                    text={t('labelCancelSelectedJobs', { count: selectedJobIds.length })}
                    variant={Variant.SOFT}
                  />
                </Stack>

                <Typography level={Level.BODY_SM} sx={{ color: 'text.tertiary' }}>
                  {t('messageConsumerRestartJobsHelp')}
                </Typography>
              </Stack>

              {/* No divider here, unlike the card above: the table brings its own shaded
                  header row, so a rule as well would draw the line twice. */}
              {isJobsError ? (
                <ConsumerLoadError
                  message={t('messageConsumerRestartJobsError')}
                  onRetry={refetchJobs}
                />
              ) : (
                <ConsumerJobsTable
                  jobs={jobs}
                  onToggleAll={onToggleAll}
                  onToggleJob={onToggleJob}
                  selectedJobIds={selectedJobIds}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Confirmation
        confirmBtnColor={Color.DANGER}
        confirmBtnDisabled={isTypedConfirmUnmet}
        isOpen={null !== pendingTarget}
        message={
          <Stack spacing={2}>
            <Typography level={Level.BODY_SM}>
              {affectedJobs.length
                ? t('messageConsumerRestartConfirm', {
                    count: affectedJobs.length,
                    env: pendingEnvLabel,
                    jobs: affectedJobs.map(({ name }) => name).join(', '),
                  })
                : t('messageConsumerRestartConfirmIdle', { env: pendingEnvLabel })}
            </Typography>

            {requiresTypedConfirm && (
              <FormControl>
                <FormLabel htmlFor={CONFIRM_INPUT_ID}>
                  {t('labelTypeEnvironmentToConfirm')}
                </FormLabel>

                <Typography
                  id={CONFIRM_HINT_ID}
                  level={Level.BODY_SM}
                  sx={{ color: 'text.tertiary' }}
                >
                  {t('messageConsumerRestartTypeToConfirm', { env: pendingEnvLabel })}
                </Typography>

                <Input
                  autoFocus
                  onChange={(event) => setConfirmText(event.target.value)}
                  placeholder={pendingEnvLabel}
                  size={Size.SM}
                  slotProps={{
                    input: { 'aria-describedby': CONFIRM_HINT_ID, id: CONFIRM_INPUT_ID },
                  }}
                  value={confirmText}
                />
              </FormControl>
            )}
          </Stack>
        }
        onCancel={onCancelRestart}
        onConfirm={onConfirmRestart}
        title={t('titleConsumerRestartConfirm')}
      />

      <Confirmation
        confirmBtnColor={Color.DANGER}
        isOpen={isCancelConfirmOpen}
        message={t(
          // Queued jobs have nothing executing them, and an orphan's consumer is
          // already gone — promising to stop a consumer would be wrong for both.
          selectedJobsKillConsumers
            ? 'messageCancelConsumerJobsConfirm'
            : 'messageCancelConsumerJobsConfirmIdle',
          { count: selectedJobIds.length }
        )}
        onCancel={() => setIsCancelConfirmOpen(false)}
        onConfirm={onConfirmCancelJobs}
        title={t('titleCancelConsumerJobsConfirm')}
      />
    </Stack>
  );
};
